import type { CreateVoucerDialogProps } from '@/features/vouchers/components/create-voucher-dialog/create-voucher-dialog';
import type { DeleteVoucerDialogProps } from '@/features/vouchers/components/delete-voucher-dialog/delete-voucher-dialog';
import type { DetailVoucherDialogProps } from '@/features/vouchers/components/detail-voucher-dialog/detail-voucher-dialog';
import type { UpdateVoucerDialogProps } from '@/features/vouchers/components/update-voucher-dialog/update-voucher-dialog';
import type { DeleteUserDialogProps } from '@/features/users/components/delete-user-dialog/delete-user-dialog';
import type { DetailUserDialogProps } from '@/features/users/components/detail-user-dialog/detail-user-dialog';
import type { EditUserDialogProps } from '@/features/users/components/edit-user-dialog/edit-user-dialog';
import type { DetailOrderDialogProps } from '@/features/orders/components/detail-order-dialog/detail-order-dialog';
import type { SetOrderPaidDialogProps } from '@/features/orders/components/set-order-paid-dialog/set-order-paid-dialog';
import type { CancelWithdrawDialogProps } from '@/features/withdraws/components/cancel-withdraw-dialog/cancel-withdraw-dialog';
import type { DetailWithdrawDialogProps } from '@/features/withdraws/components/detail-withdraw-dialog/detail-withdraw-dialog';
import type { UploadTransferProofDialogProps } from '@/features/withdraws/components/upload-transfer-proof-dialog/upload-transfer-proof-dialog';
import type { DetailSessionDialogProps } from '@/features/sessions/components/detail-session-dialog/detail-session-dialog';
import type { ArchiveProductDialogProps } from '@/features/products/components/archive-product-dialog/archive-product-dialog';
import type { DeleteProductDialogProps } from '@/features/products/components/delete-product-dialog/delete-product-dialog';
import type { PrivateProductDialogProps } from '@/features/products/components/private-product-dialog/private-product-dialog';
import type { PublishProductDialogProps } from '@/features/products/components/publish-product-dialog/publish-product-dialog';
import type { DeleteQuestionBankDialogProps } from '@/features/question-banks/components/delete-question-bank-dialog/delete-question-bank-dialog';
import type { CreateProductClaimDialogProps } from '@/features/product-claim/components/create-product-claim-dialog/create-product-claim-dialog';
import type { UpdateProductClaimDialogProps } from '@/features/product-claim/components/update-product-claim-dialog/update-product-claim-dialog';
import type { DeleteProductClaimDialogProps } from '@/features/product-claim/components/delete-product-claim-dialog/delete-product-claim-dialog';
import type { CreateAdminDialogProps } from '@/features/admins/components/create-admin-dialog/create-admin-dialog';
import type { DetailAdminDialogProps } from '@/features/admins/components/detail-admin-dialog/detail-admin-dialog';
import type { EditAdminDialogProps } from '@/features/admins/components/edit-admin-dialog/edit-admin-dialog';
import type { DeleteAdminDialogProps } from '@/features/admins/components/delete-admin-dialog/delete-admin-dialog';
import type { ChangeAdminPasswordDialogProps } from '@/features/admins/components/change-admin-password-dialog/change-admin-password-dialog';
import type { CreateRoleDialogProps } from '@/features/roles/components/create-role-dialog/create-role-dialog';
import type { DetailRoleDialogProps } from '@/features/roles/components/detail-role-dialog/detail-role-dialog';
import type { EditRoleDialogProps } from '@/features/roles/components/edit-role-dialog/edit-role-dialog';
import type { DeleteRoleDialogProps } from '@/features/roles/components/delete-role-dialog/delete-role-dialog';
import type { DetailNotificationDeliveryDialogProps } from '@/features/notification-deliveries/components/detail-notification-delivery-dialog/detail-notification-delivery-dialog';
import { emptyFn } from '@/utils/app';
import { createContext, useContext } from 'react';

export interface V2DialogContext {
  // Create Voucher dialog
  openCreateVoucherDialog: (
    props?: Omit<CreateVoucerDialogProps, 'dialog'>
  ) => void;
  closeCreateVoucherDialog: () => void;

  // Update Voucher dialog
  openUpdateVoucherDialog: (
    props?: Omit<UpdateVoucerDialogProps, 'dialog'>
  ) => void;
  closeUpdateVoucherDialog: () => void;

  // Delete Voucher dialog
  openDeleteVoucherDialog: (
    props?: Omit<DeleteVoucerDialogProps, 'dialog'>
  ) => void;
  closeDeleteVoucherDialog: () => void;

  // Detail Voucher dialog
  openDetailVoucherDialog: (
    props?: Omit<DetailVoucherDialogProps, 'dialog'>
  ) => void;
  closeDetailVoucherDialog: () => void;

  // Detail User dialog
  openDetailUserDialog: (props?: Omit<DetailUserDialogProps, 'dialog'>) => void;
  closeDetailUserDialog: () => void;

  // Edit User dialog
  openEditUserDialog: (props?: Omit<EditUserDialogProps, 'dialog'>) => void;
  closeEditUserDialog: () => void;

  // Delete User dialog
  openDeleteUserDialog: (props?: Omit<DeleteUserDialogProps, 'dialog'>) => void;
  closeDeleteUserDialog: () => void;

  // Detail Order dialog
  openDetailOrderDialog: (
    props?: Omit<DetailOrderDialogProps, 'dialog'>
  ) => void;
  closeDetailOrderDialog: () => void;

  // Set Order Paid dialog
  openSetOrderPaidDialog: (
    props?: Omit<SetOrderPaidDialogProps, 'dialog'>
  ) => void;
  closeSetOrderPaidDialog: () => void;

  // Detail Withdraw dialog
  openDetailWithdrawDialog: (
    props?: Omit<DetailWithdrawDialogProps, 'dialog'>
  ) => void;
  closeDetailWithdrawDialog: () => void;

  // Upload Transfer Proof dialog
  openUploadTransferProofDialog: (
    props?: Omit<UploadTransferProofDialogProps, 'dialog'>
  ) => void;
  closeUploadTransferProofDialog: () => void;

  // Cancel Withdraw dialog
  openCancelWithdrawDialog: (
    props?: Omit<CancelWithdrawDialogProps, 'dialog'>
  ) => void;
  closeCancelWithdrawDialog: () => void;

  // Detail Session dialog
  openDetailSessionDialog: (
    props?: Omit<DetailSessionDialogProps, 'dialog'>
  ) => void;
  closeDetailSessionDialog: () => void;

  // Delete Product dialog
  openDeleteProductDialog: (
    props?: Omit<DeleteProductDialogProps, 'dialog'>
  ) => void;
  closeDeleteProductDialog: () => void;

  // Publish Product dialog
  openPublishProductDialog: (
    props?: Omit<PublishProductDialogProps, 'dialog'>
  ) => void;
  closePublishProductDialog: () => void;

  // Archive Product dialog
  openArchiveProductDialog: (
    props?: Omit<ArchiveProductDialogProps, 'dialog'>
  ) => void;
  closeArchiveProductDialog: () => void;

  // Private Product dialog
  openPrivateProductDialog: (
    props?: Omit<PrivateProductDialogProps, 'dialog'>
  ) => void;
  closePrivateProductDialog: () => void;

  // Delete Question Bank dialog
  openDeleteQuestionBankDialog: (
    props?: Omit<DeleteQuestionBankDialogProps, 'dialog'>
  ) => void;
  closeDeleteQuestionBankDialog: () => void;

  // Create Klaim Produk dialog
  openCreateProductClaimDialog: (
    props?: Omit<CreateProductClaimDialogProps, 'dialog'>
  ) => void;
  closeCreateProductClaimDialog: () => void;

  // Update Klaim Produk dialog
  openUpdateProductClaimDialog: (
    props?: Omit<UpdateProductClaimDialogProps, 'dialog'>
  ) => void;
  closeUpdateProductClaimDialog: () => void;

  // Delete Klaim Produk dialog
  openDeleteProductClaimDialog: (
    props?: Omit<DeleteProductClaimDialogProps, 'dialog'>
  ) => void;
  closeDeleteProductClaimDialog: () => void;

  // Create Admin dialog
  openCreateAdminDialog: (
    props?: Omit<CreateAdminDialogProps, 'dialog'>
  ) => void;
  closeCreateAdminDialog: () => void;

  // Detail Admin dialog
  openDetailAdminDialog: (
    props?: Omit<DetailAdminDialogProps, 'dialog'>
  ) => void;
  closeDetailAdminDialog: () => void;

  // Edit Admin dialog
  openEditAdminDialog: (props?: Omit<EditAdminDialogProps, 'dialog'>) => void;
  closeEditAdminDialog: () => void;

  // Delete Admin dialog
  openDeleteAdminDialog: (
    props?: Omit<DeleteAdminDialogProps, 'dialog'>
  ) => void;
  closeDeleteAdminDialog: () => void;

  // Change Admin Password dialog
  openChangeAdminPasswordDialog: (
    props?: Omit<ChangeAdminPasswordDialogProps, 'dialog'>
  ) => void;
  closeChangeAdminPasswordDialog: () => void;

  // Create Role dialog
  openCreateRoleDialog: (props?: Omit<CreateRoleDialogProps, 'dialog'>) => void;
  closeCreateRoleDialog: () => void;

  // Detail Role dialog
  openDetailRoleDialog: (props?: Omit<DetailRoleDialogProps, 'dialog'>) => void;
  closeDetailRoleDialog: () => void;

  // Edit Role dialog
  openEditRoleDialog: (props?: Omit<EditRoleDialogProps, 'dialog'>) => void;
  closeEditRoleDialog: () => void;

  // Delete Role dialog
  openDeleteRoleDialog: (props?: Omit<DeleteRoleDialogProps, 'dialog'>) => void;
  closeDeleteRoleDialog: () => void;

  // Detail Notification Delivery dialog
  openDetailNotificationDeliveryDialog: (
    props?: Omit<DetailNotificationDeliveryDialogProps, 'dialog'>
  ) => void;
  closeDetailNotificationDeliveryDialog: () => void;
}

export const V2dialogContext = createContext<V2DialogContext>({
  // Create Voucher dialog
  openCreateVoucherDialog: emptyFn,
  closeCreateVoucherDialog: emptyFn,

  // Update Voucher dialog
  openUpdateVoucherDialog: emptyFn,
  closeUpdateVoucherDialog: emptyFn,

  // Delete Voucher dialog
  openDeleteVoucherDialog: emptyFn,
  closeDeleteVoucherDialog: emptyFn,

  // Detail Voucher dialog
  openDetailVoucherDialog: emptyFn,
  closeDetailVoucherDialog: emptyFn,

  // Detail User dialog
  openDetailUserDialog: emptyFn,
  closeDetailUserDialog: emptyFn,

  // Edit User dialog
  openEditUserDialog: emptyFn,
  closeEditUserDialog: emptyFn,

  // Delete User dialog
  openDeleteUserDialog: emptyFn,
  closeDeleteUserDialog: emptyFn,

  // Detail Order dialog
  openDetailOrderDialog: emptyFn,
  closeDetailOrderDialog: emptyFn,

  // Set Order Paid dialog
  openSetOrderPaidDialog: emptyFn,
  closeSetOrderPaidDialog: emptyFn,

  // Detail Withdraw dialog
  openDetailWithdrawDialog: emptyFn,
  closeDetailWithdrawDialog: emptyFn,

  // Upload Transfer Proof dialog
  openUploadTransferProofDialog: emptyFn,
  closeUploadTransferProofDialog: emptyFn,

  // Cancel Withdraw dialog
  openCancelWithdrawDialog: emptyFn,
  closeCancelWithdrawDialog: emptyFn,

  // Detail Session dialog
  openDetailSessionDialog: emptyFn,
  closeDetailSessionDialog: emptyFn,

  // Delete Product dialog
  openDeleteProductDialog: emptyFn,
  closeDeleteProductDialog: emptyFn,

  // Publish Product dialog
  openPublishProductDialog: emptyFn,
  closePublishProductDialog: emptyFn,

  // Archive Product dialog
  openArchiveProductDialog: emptyFn,
  closeArchiveProductDialog: emptyFn,

  // Private Product dialog
  openPrivateProductDialog: emptyFn,
  closePrivateProductDialog: emptyFn,

  // Delete Question Bank dialog
  openDeleteQuestionBankDialog: emptyFn,
  closeDeleteQuestionBankDialog: emptyFn,

  // Create Klaim Produk dialog
  openCreateProductClaimDialog: emptyFn,
  closeCreateProductClaimDialog: emptyFn,

  // Update Klaim Produk dialog
  openUpdateProductClaimDialog: emptyFn,
  closeUpdateProductClaimDialog: emptyFn,

  // Delete Klaim Produk dialog
  openDeleteProductClaimDialog: emptyFn,
  closeDeleteProductClaimDialog: emptyFn,

  // Create Admin dialog
  openCreateAdminDialog: emptyFn,
  closeCreateAdminDialog: emptyFn,

  // Detail Admin dialog
  openDetailAdminDialog: emptyFn,
  closeDetailAdminDialog: emptyFn,

  // Edit Admin dialog
  openEditAdminDialog: emptyFn,
  closeEditAdminDialog: emptyFn,

  // Delete Admin dialog
  openDeleteAdminDialog: emptyFn,
  closeDeleteAdminDialog: emptyFn,

  // Change Admin Password dialog
  openChangeAdminPasswordDialog: emptyFn,
  closeChangeAdminPasswordDialog: emptyFn,

  // Create Role dialog
  openCreateRoleDialog: emptyFn,
  closeCreateRoleDialog: emptyFn,

  // Detail Role dialog
  openDetailRoleDialog: emptyFn,
  closeDetailRoleDialog: emptyFn,

  // Edit Role dialog
  openEditRoleDialog: emptyFn,
  closeEditRoleDialog: emptyFn,

  // Delete Role dialog
  openDeleteRoleDialog: emptyFn,
  closeDeleteRoleDialog: emptyFn,

  // Detail Notification Delivery dialog
  openDetailNotificationDeliveryDialog: emptyFn,
  closeDetailNotificationDeliveryDialog: emptyFn,
});

export const useV2DialogContext = () => useContext(V2dialogContext);
